import {
  Button,
  CircularProgress,
  Divider,
  Tag,
  Typography,
} from "@suraasa/placebo-ui"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useMediaQuery } from "@uidotdev/usehooks"
import api from "api"
import { queries } from "api/queries"
import DEFAULT_MASTERCLASS_IMAGE from "assets/Placeholder/default-masterclass-thumbnail.svg"
import FullPageLoading from "components/FullPageLoading"
import PageContainer from "components/PageContainer"
import ShareSheet from "components/ShareSheet"
import MasterclassCard from "features/Masterclasses/MasterclassCard"
import { getMasterclassStatus } from "features/Masterclasses/UpcomingMasterclass/helpers"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { useGlobalContext } from "global/Context/context"
import { NavArrowRight, ShareAndroid } from "iconoir-react"
import { useEffect, useState } from "react"
import { Helmet } from "react-helmet"
import { Link, useParams } from "react-router-dom"
import routes from "routes"
import { formatDate, getPlatformURL, mapErrorsV2 } from "utils/helpers"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

import Attended from "./Attended"
import ProgressMasterclass from "./MasterclassInProgressCard"
import ProcessingMasterclass from "./ProcessingMasterclass"
import RegisterMasterclass from "./RegisterMasterclass"
import ConfirmationScreen from "./SuraasaPlus/ConfirmationScreen"
import CreditBanner from "./SuraasaPlus/CreditBanner"
import PauseScreen from "./SuraasaPlus/PauseScreen"
import TrySuraasaPlusScreen from "./SuraasaPlus/TrySuraasaPlusScreen"
import MasterclassRecordingVideoPlayer from "./VideoPlayer"

const getShareURL = ({ slug }: { slug: string }) => {
  return getPlatformURL("suraasa", `/masterclasses/${slug}`)
}

const formatDuration = (duration: string) => {
  const [hours, minutes] = duration.split(":").map(_ => parseInt(_))

  const hDisplay = hours > 0 ? `${hours}h ` : ""
  const mDisplay = minutes > 0 ? `${minutes}m` : ""

  return (hDisplay + mDisplay).trim()
}

function createMarkup(data: string) {
  return {
    __html: data,
  }
}

const ViewMasterclass = () => {
  const [shareDialog, setShareDialog] = useState(false)
  const { slug = "" } = useParams<{ slug: string }>()

  const isSmDown = useMediaQuery("only screen and (max-width : 640px)")

  const { isGrowthSpaceUser } = useGlobalContext()
  const { isActive } = useSuraasaPlusContext()

  const {
    data: previousMasterclasses,
    isLoading: previousMasterclassesLoading,
  } = useQuery({
    queryKey: queries.masterclasses.masterclassesList({
      page: 1,
      size: 3,
      status: "past",
      exclude: slug,
    }).queryKey,
    queryFn: () =>
      api.masterclasses.list({
        params: {
          page: 1,
          size: 3,
          status: "past",
          exclude: slug,
        },
      }),
  })

  const {
    data: masterclass,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queries.masterclasses.getMasterClassDetails(slug).queryKey,
    queryFn: () =>
      api.masterclasses.getItoMasterclass({
        urlParams: {
          slug: slug,
        },
      }),
  })

  // Track page view when masterclass loads
  useEffect(() => {
    if (masterclass) {
      const status = getMasterclassStatus(masterclass)
      trackingService.trackEvent(
        TrackingEvents.masterclassDetailViewed,
        {
          masterclass_id: masterclass.id,
          masterclass_slug: masterclass.slug,
          masterclass_title: masterclass.title,
          status: status,
          has_recording: masterclass.hasRecording,
          is_registered: masterclass.isRegistered,
          has_attended: masterclass.hasAttended,
        },
        { services: ["PostHog"] }
      )
    }
  }, [masterclass])

  const triggerConfirmWatch = useMutation({
    mutationKey: ["confirmFreeWatch", masterclass?.slug],
    mutationFn: () =>
      api.masterclasses.watchFreeMasterclass({
        urlParams: { masterclassSlug: masterclass?.slug || slug },
      }),
    onError: e => {
      mapErrorsV2(e)
    },
  })

  const isFreeUser = !isGrowthSpaceUser && !isActive

  const isCreditLeft = masterclass?.userRestriction.consumptionAllowed
  const creditValidity = masterclass?.userRestriction.validity

  const [now] = useState(() => Date.now())

  const isCreditValidityActive =
    !!creditValidity && new Date(creditValidity).getTime() > now

  const status = getMasterclassStatus(masterclass)
  const pageTitle = masterclass?.title || "Masterclass Details"

  const toggleShareDialog = () => {
    if (!shareDialog) {
      trackingService.trackEvent(
        TrackingEvents.masterclassShareClicked,
        {
          masterclass_id: masterclass?.id,
          masterclass_slug: masterclass?.slug,
        },
        { services: ["PostHog"] }
      )
    }
    setShareDialog(prev => !prev)
  }

  const getMainContent = () => {
    if (!masterclass) {
      return null
    }

    const { thumbnail, recordingUrl, hasRecording } = masterclass

    const subContent = (
      <>
        {masterclass.hasAttended && <Attended id={masterclass.id} />}

        {isCreditLeft && isCreditValidityActive && (
          <div className="mt-2">
            <CreditBanner duration={creditValidity} />
          </div>
        )}

        <div className="mt-3 flex flex-col justify-between gap-y-0.5 sm:mt-4.5 sm:flex-row">
          <Typography variant="title2" className="first-letter-capitalize">
            {masterclass.title}
          </Typography>
          <div className="mt-0.5 flex gap-0.5 sm:mt-0">
            <Typography variant="strong">
              {status === "upcoming" ? "Will be held on" : "Held on"}{" "}
            </Typography>
            <Typography variant="body">
              {formatDate(masterclass.startTime, "d MMMM, yyyy")}
            </Typography>
          </div>
        </div>

        <Tag className="mt-1" color="onSurface">
          {masterclass.recordingDuration
            ? formatDuration(masterclass.recordingDuration)
            : formatDuration(masterclass.duration)}
        </Tag>
      </>
    )

    if (status === "upcoming") {
      return (
        <RegisterMasterclass
          masterclass={masterclass}
          refetchMasterclass={refetch}
        />
      )
    }

    if (hasRecording) {
      if (isFreeUser) {
        if (isCreditLeft && !isCreditValidityActive) {
          return (
            <>
              <ConfirmationScreen
                masterclassSlug={masterclass.slug}
                refetchMasterclass={refetch}
                thumbnail={thumbnail ?? DEFAULT_MASTERCLASS_IMAGE}
              />
              {subContent}
            </>
          )
        }

        if (!isCreditLeft) {
          return (
            <>
              <TrySuraasaPlusScreen
                thumbnail={thumbnail ?? DEFAULT_MASTERCLASS_IMAGE}
              />
              {subContent}
            </>
          )
        }
      }

      if (recordingUrl)
        return (
          <>
            <MasterclassRecordingVideoPlayer
              triggerConfirmWatch={triggerConfirmWatch.mutate}
              id={masterclass.id}
              title={masterclass.title}
              slug={masterclass.slug}
              poster={thumbnail ?? DEFAULT_MASTERCLASS_IMAGE}
              src={recordingUrl}
              thumbnailComponent={
                isFreeUser ? (
                  <PauseScreen
                    creditValidity={
                      isCreditValidityActive ? creditValidity : undefined
                    }
                    thumbnail={thumbnail ?? DEFAULT_MASTERCLASS_IMAGE}
                  />
                ) : undefined
              }
            />
            {subContent}
          </>
        )

      return null
    }

    if (status === "ongoing") {
      return <ProgressMasterclass masterclass={masterclass} />
    }

    if (status === "completed") {
      return (
        <>
          <ProcessingMasterclass masterclass={masterclass} />
          {masterclass.hasAttended && <Attended id={masterclass.id} />}
        </>
      )
    }
  }

  if (isLoading) return <FullPageLoading />

  return (
    <div>
      {/* @ts-expect-error react version issues */}
      <Helmet title={pageTitle}>
        <title>{pageTitle}</title>
      </Helmet>
      <PageContainer className="border-b border-onSurface-200 bg-surface pb-2 pt-3">
        <div className="flex w-full flex-col items-start justify-between sm:flex-row sm:items-center">
          <div className="w-full sm:max-w-[70%] md:max-w-[80%]">
            <div className="mb-2.5 flex items-center gap-1 sm:mb-1.5">
              <Link to={routes.masterclasses}>
                <Typography
                  variant="smallBody"
                  className="min-w-12 text-onSurface-400"
                >
                  Masterclasses
                </Typography>
              </Link>
              <NavArrowRight className="!size-2 text-onSurface-400" />
              <Typography
                variant="smallBody"
                className="first-letter-capitalize"
              >
                {masterclass?.title}
              </Typography>
            </div>
            <Typography variant="title2" className="first-letter-capitalize">
              {masterclass?.title}
            </Typography>
          </div>
          <Button
            className="mt-2 border border-onSurface-300 text-onSurface-700 shadow-[0px_1px_2px_0px_rgba(113,113,122,0.08)] sm:mt-0"
            endAdornment={<ShareAndroid className="!size-2" />}
            variant="text"
            onClick={() => toggleShareDialog()}
          >
            {isSmDown ? "Share" : "Share Masterclass"}
          </Button>
        </div>
        <ShareSheet
          data={{
            url: getShareURL({ slug }),
            text: `Checkout this masterclass on Suraasa: ${getShareURL({
              slug,
            })}`,
          }}
          open={shareDialog}
          platforms={["whatsapp", "telegram", "linkedin", "facebook"]}
          handleClose={toggleShareDialog}
        />
      </PageContainer>

      <div className="bg-surface pb-10 pt-3.5">
        {masterclass ? (
          <PageContainer>
            {getMainContent()}

            {masterclass.description && (
              <div className="mb-4.5 mt-3">
                <Typography
                  className="mb-1"
                  color="onSurface.900"
                  variant="strong"
                >
                  Description
                </Typography>
                <div
                  className="prose prose-sm max-w-none text-onSurface-700
                 [&>li]:mb-1 [&>ul]:list-disc [&>ul]:pl-3"
                  dangerouslySetInnerHTML={createMarkup(
                    masterclass.description
                  )}
                />
              </div>
            )}

            <Divider />
            <div className="mt-4.5">
              <Typography variant="title3">Previous Masterclasses</Typography>
              {previousMasterclassesLoading ? (
                <div className="mt-2 grid grid-cols-12 gap-3">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div
                      className="col-span-12 sm:col-span-6 md:col-span-4"
                      key={index}
                    >
                      <div className="animate-pulse rounded-lg border border-onSurface-200 bg-surface p-2">
                        <div className="mb-2 h-20 w-full rounded-md bg-onSurface-200" />
                        <div className="mb-1 h-2 w-3/4 rounded bg-onSurface-200" />
                        <div className="h-1.75 w-1/2 rounded bg-onSurface-200" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : previousMasterclasses?.data &&
                previousMasterclasses.data.length > 0 ? (
                <div className="mt-2 grid grid-cols-12 gap-3">
                  {previousMasterclasses.data.map((classDetails, index) => (
                    <div
                      className="col-span-12 sm:col-span-6 md:col-span-4"
                      key={index}
                    >
                      <MasterclassCard masterclass={classDetails} />
                    </div>
                  ))}
                </div>
              ) : (
                <></>
              )}
            </div>
          </PageContainer>
        ) : (
          <div className="mt-10 flex min-h-[60vh] justify-center">
            <CircularProgress />
          </div>
        )}
      </div>
    </div>
  )
}

export default ViewMasterclass
